import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ErrorBanner, Spinner } from '../../Admin/shared';
import { useReferralOverview } from './api/useReferralOverview';
import { DateRangeControl, defaultRange, priorRange } from '@/components/shared';
import { useAdminFetch } from '../../Admin/shared/useAdminFetch';
import type { ReferralOverview } from './api/types';
import { FunnelStrip } from './components/FunnelStrip';
import { ChannelChart } from './components/ChannelChart';
import { EntryChart } from './components/EntryChart';
import { TopReferrers } from './components/TopReferrers';
import { ReferralList } from './components/ReferralList';

/**
 * Referral analytics overview: date window, funnel strip, the channel and
 * entry-point breakdowns, and the top-referrers leaderboard. Every section
 * reads its own independently-nullable slice of `ReferralOverview` and is
 * responsible for its own "Not available" state — a failure in one section
 * (say, `by_channel`) never hides the others.
 *
 * Opens on the same 30-day window as every other analytics page. This
 * programme has few attributions, so a quiet month can look empty — "All time"
 * is one click away on the shared `DateRangeControl`.
 */
export function ReferralAnalyticsPage(): React.ReactElement {
  const [from, setFrom] = useState<string | null>(() => defaultRange()[0]);
  const [to, setTo] = useState<string | null>(() => defaultRange()[1]);
  const isAllTime = from === null && to === null;

  const { data, loading, error } = useReferralOverview({
    from: from ?? undefined,
    to: to ?? undefined,
  });
  const before = priorRange(from, to);
  const prior = useAdminFetch<ReferralOverview>(
    before && {
      env: 'prod',
      path: '/admin/analytics/referrals/overview',
      query: { from: before[0], to: before[1] },
    },
  );

  return (
    <div className="min-h-screen bg-gray-950">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-5">
        {/* Same back-link treatment the per-user detail pages use, so moving
            up a level looks the same wherever you are in this area. */}
        <div>
          <Link
            to="/analytics"
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
          >
            ← Back to analytics
          </Link>
        </div>

        <header className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold text-white tracking-tight">Referral Analytics</h1>
            <p className="mt-1 text-xs text-gray-500">
              How referral codes turn into counted, verified referrals.
            </p>
          </div>
          <DateRangeControl from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
        </header>

        {error && <ErrorBanner message={error} />}
        {loading && !data && (
          <div className="flex justify-center py-24">
            <Spinner />
          </div>
        )}

        {data && (
          <>
            <FunnelStrip funnel={data.funnel} prior={prior?.funnel ?? null} isAllTime={isAllTime} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <section className="rounded-2xl border border-gray-700 bg-gray-900 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-700">
                  <h2 className="text-sm font-semibold text-gray-200">Referrals by channel</h2>
                </div>
                <div className="px-5 py-4">
                  <ChannelChart byChannel={data.by_channel} channelScope={data.channel_scope} />
                </div>
              </section>

              <section className="rounded-2xl border border-gray-700 bg-gray-900 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-700">
                  <h2 className="text-sm font-semibold text-gray-200">Referrals by entry point</h2>
                  <p className="mt-1 text-[11px] text-gray-500">
                    Signup vs. settings-page redemptions.
                  </p>
                </div>
                <div className="px-5 py-4">
                  <EntryChart byEntry={data.by_entry} />
                </div>
              </section>
            </div>

            <TopReferrers topReferrers={data.top_referrers} />
          </>
        )}

        {/* The list is its own server-side query (filters, sort, paging all
            live server-side) — it shares the page's date window but doesn't
            depend on the overview having loaded, so a failure up there never
            blanks out the list below it. */}
        <ReferralList from={from} to={to} />
      </div>
    </div>
  );
}

export default ReferralAnalyticsPage;
